import { Aggregate } from "@interfaces/analysis";
import { Box, Stack, Typography } from "@mui/material";
import { nFormatter } from "@utils/number";
import * as d3 from "d3";
import { forwardRef, useEffect } from "react";

interface PieChartProps {
  title: string;

  data: Aggregate;
  onClick: () => void;
}

export const PieChart = forwardRef(
  ({ title, data, onClick }: PieChartProps, ref) => {
    const { target, achieved, allocated } = data;
    const percentageAchieved = target > 0 ? (achieved / target) * 100 : 0;
    const chartId = `pie-${Math.random()
      .toString()
      .replaceAll(".", "-")
      .toLocaleLowerCase()}-pie-chart`;
    const buildGraph = () => {
      const data = [
        {
          label: "Achieved",
          index: 0,
          value: achieved,
          color: "#009CFF",
        },
        {
          label: "Remaining",
          index: 1,
          value: target ? target - achieved : 100,
          color: "#FF0000",
        },
      ];

      const $this = `#${chartId}`;

      d3.select($this).selectAll("svg").remove();

      const width = 200,
        height = 100,
        radius = 80,
        innerradius = 60;

      const arc = d3.arc().outerRadius(radius).innerRadius(innerradius);

      const pie = d3
        .pie()
        .sort(null)
        .value((d: any) => {
          return d.value;
        })
        .startAngle(-Math.PI / 2)
        .endAngle(Math.PI / 2);

      const svg = d3
        .select($this)
        .append("svg")
        .attr("width", width)
        .attr("height", height)
        .style("background", "white")
        .append("g")
        .attr("class", "piechart");

      const segments = svg.append("g").attr("class", "segments");

      const slices = segments
        .selectAll(".arc")
        .data(pie(data as any))
        .enter()
        .append("g")
        .attr("class", "arc");

      slices
        .append("path")
        .attr("d", arc as any)
        .attr("fill", function (d: any, i: any) {
          return d.data.color;
        })
        .attr(
          "transform",
          "translate(" + width / 2 + "," + ((9 * height) / 10 - 10) + ")"
        );

      const getCenterOffset = (text: string) => {
        if (text.length < 2) {
          return -20;
        }
        return -30;
      };
      const guageLabel = `${percentageAchieved.toFixed(1)}%`;
      svg
        .append("text")
        .style("font", '20px "Montserrat", sans-serif')
        .text(guageLabel)
        .attr("x", getCenterOffset(guageLabel))
        .attr("y", 0)
        .attr("fill", function (d: any, i: any) {
          return "#009CFF";
        })
        .attr("transform", "translate(" + width / 2 + "," + height + ")");

      svg
        .append("text")
        .style("font", '12px "Montserrat", sans-serif')
        .text(`0`)
        .attr("x", -width / 2 + 25)
        .attr("y", 0)
        .attr("fill", function (d: any, i: any) {
          return "#009CFF";
        })
        .attr("transform", "translate(" + width / 2 + "," + height + ")");

      const getSecondLabelOffset = (text: string) => {
        if (text.length < 2) {
          return width / 2 - 35;
        }
        return width / 2 - 10;
      };
      const secondLabel = nFormatter(target, 2);
      svg
        .append("text")
        .style("font", '12px "Montserrat", sans-serif')
        .text(secondLabel)
        .attr("x", getSecondLabelOffset(secondLabel))
        .attr("y", 0)
        .attr("fill", function (d: any, i: any) {
          return "#009CFF";
        })
        .attr("transform", "translate(" + width / 2 + "," + height + ")");
    };

    useEffect(() => {
      // Update the document title using the browser API
      buildGraph();
    });

    return (
      <Box
        onClick={onClick}
        component={"div"}
        ref={ref}
        sx={{
          margin: "auto",
          maxWidth: 200,
          maxHeight: 200,
          textAlign: "center",
          justifyContent: "center",
          alignContent: "center",
          paddingTop: "20px",
          paddingBotton: "20px",
        }}
      >
        <Stack
          component={"div"}
          sx={{
            margin: 0,
            justifyContent: "center",
            alignContent: "center",
          }}
          spacing={2}
        >
          <div id={chartId} />
          <Box>
            <Typography
              variant="body1"
              sx={{ color: "#009CFF", paddingBotton: "20px" }}
            >
              {title}
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  }
);
